import React from 'react';

import {Button, ModalPopup, Select} from '@kit';

import {WeaponProperty} from '../../../../generated/types';
import {cls} from '../../../../components/utils';
import {useCharacter} from '../character-context';
import {EquippedItem, EquipSlot, equipSlot, hasProperty, isWeapon, stackEquipment} from '../model/equipment';
import {useEquipmentActions} from './equipment-row';
import {InlineCheck} from './inline-check';

import css from './sheet.module.css';

type Props = {
	onClose(): void;
};

const SLOT_TITLES: Record<EquipSlot, string> = {
	armor: 'Доспех',
	shield: 'Щит',
};

const NOTHING = '';

export const PaperDollModal: React.FC<Props> = ({onClose}) => {
	const {character} = useCharacter();
	const {equipment} = character;
	const worn = (slot: EquipSlot) => equipment.find(({entry, item}) => entry.equipped && equipSlot(item) === slot);
	const weapons = equipment.filter(({item}) => isWeapon(item));
	const otherItems = equipment.filter(({item}) => !isWeapon(item) && !equipSlot(item));
	const armor = worn('armor');
	const shield = worn('shield');
	const hasWeapon = weapons.some(({entry}) => entry.equipped && !entry.offHand);

	return (
		<ModalPopup open onClose={onClose} header="Экипировка" maxWidth={880}>
			<div className={css.paperDoll}>
				<div className={css.dollColumn}>
					<SlotSelect slot="armor" />
					<SlotSelect slot="shield" />
					<p className={css.muted}>Доспех и щит можно надеть только по одному.</p>
				</div>
				<figure className={css.dollFigure} aria-label="Персонаж">
					<CharacterSilhouette armor={!!armor} shield={!!shield} weapon={hasWeapon} />
					{armor && <span className={cls(css.dollLabel, css.dollArmorLabel)}>{armor.item.title}</span>}
					{shield && <span className={cls(css.dollLabel, css.dollShieldLabel)}>{shield.item.title}</span>}
				</figure>
				<div className={css.dollColumn}>
					<CheckList title="Оружие" items={weapons} empty="Оружия нет" />
				</div>
			</div>
			<CheckList title="Надетые предметы" items={otherItems} empty="Других предметов нет" />
			<div className={css.modalButtons}>
				<Button appearance="primary" onClick={onClose}>
					Готово
				</Button>
			</div>
		</ModalPopup>
	);
};

const SlotSelect: React.FC<{slot: EquipSlot}> = ({slot}) => {
	const {character} = useCharacter();
	const {setEquipped} = useEquipmentActions();
	const candidates = character.equipment.filter(({item}) => equipSlot(item) === slot);
	const current = candidates.find(({entry}) => entry.equipped);

	const select = (entryKey: string) => {
		if (entryKey) setEquipped([entryKey], true);
		else if (current) setEquipped([current.entry.key], false);
	};

	return (
		<Select
			label={SLOT_TITLES[slot]}
			placeholder={candidates.length ? 'Не надет' : 'Нет в снаряжении'}
			disabled={!candidates.length}
			items={[
				{value: NOTHING, label: 'Не надет'},
				...candidates.map(({entry, item}) => ({value: entry.key, label: item.title})),
			]}
			value={current?.entry.key ?? NOTHING}
			disableClear
			onChange={([value]) => select(String(value ?? NOTHING))}
		/>
	);
};

const CheckList: React.FC<{title: string; items: EquippedItem[]; empty: string}> = ({title, items, empty}) => {
	const {setEquipped, setStackOffHand} = useEquipmentActions();
	return (
		<section className={css.dollList}>
			<h3 className={css.groupTitle}>{title}</h3>
			{items.length ? (
				stackEquipment(items, {byItem: true}).map((stack) => {
					const {item, isAttuned} = stack.entries[0];
					const main = stack.entries.filter(({entry}) => !entry.offHand).map(({entry}) => entry.key);
					const inUse = stack.entries.some(({entry}) => entry.equipped && !entry.offHand);
					const offHand = stack.entries.some(({entry}) => entry.offHand);
					return (
						<React.Fragment key={stack.key}>
							<InlineCheck checked={inUse} onChange={(value) => setEquipped(main, value)}>
								{item.title}
								{stack.quantity > 1 && <span className={css.muted}> × {stack.quantity}</span>}
								{item.requiresAttunement && !isAttuned && (
									<span className={css.muted}> · нужна настройка</span>
								)}
							</InlineCheck>
							{isWeapon(item) && hasProperty(item, WeaponProperty.Light) && (
								<InlineCheck checked={offHand} onChange={(value) => setStackOffHand(stack, value)}>
									<span className={css.muted}>{item.title} во второй руке</span>
								</InlineCheck>
							)}
						</React.Fragment>
					);
				})
			) : (
				<p className={css.muted}>{empty}</p>
			)}
		</section>
	);
};

type SilhouetteProps = {
	armor: boolean;
	shield: boolean;
	weapon: boolean;
};

const CharacterSilhouette: React.FC<SilhouetteProps> = ({armor, shield, weapon}) => (
	<svg viewBox="0 0 200 320" className={css.dollSvg} role="img" aria-hidden>
		<circle cx="100" cy="42" r="26" className={css.dollBody} />
		<path
			d="M62 84 Q100 70 138 84 L150 180 Q100 196 50 180 Z"
			className={cls(css.dollBody, armor && css.dollWorn)}
		/>
		<path d="M62 88 L34 170 L46 176 L74 104 Z" className={css.dollBody} />
		<path d="M138 88 L166 170 L154 176 L126 104 Z" className={css.dollBody} />
		<path d="M64 184 L58 300 L84 300 L98 196 Z" className={css.dollBody} />
		<path d="M136 184 L142 300 L116 300 L102 196 Z" className={css.dollBody} />
		{weapon && <path d="M36 176 L20 110 L26 108 L44 172 Z" className={css.dollWorn} />}
		{shield && (
			<path
				d="M150 150 Q176 146 188 156 Q188 196 164 212 Q142 196 142 158 Z"
				className={cls(css.dollBody, css.dollWorn)}
			/>
		)}
	</svg>
);
